import { action, computed, observable, runInAction } from 'mobx'
import Scheduler from '@/utils/Scheduler.js'
import { JITSI_PAYMENT_CHECK_INTERVAL } from '@/constants/jitsi.js'

export default class JitsiLowLevelApiStore {
  #jitsi = null
  #client = null
  #checkPaymentScheduler = null

  @observable accessor #connection = null
  @observable accessor #room = null
  @observable accessor id = ''
  @observable accessor isModerator = false

  constructor(jitsiStore) {
    this.#jitsi = jitsiStore
    this.#checkPaymentScheduler = new Scheduler(
      this.#checkPayment,
      JITSI_PAYMENT_CHECK_INTERVAL,
      true
    )

    if (!window.JitsiMeetJS) throw new Error('[Jitsi] Jitsi not available')

    this.#client = window.JitsiMeetJS
    delete window.JitsiMeetJS

    this.#client.init()
  }

  deconstructor = () => {
    this.#checkPaymentScheduler.deconstructor()
  }

  init = async () => {
    await this.reset()

    this.#connection = new this.#client.JitsiConnection(null, this.#jitsi.jwt, {
      hosts: {
        domain: import.meta.env.VITE_JITSI_DOMAIN,
        muc: `conference.${import.meta.env.VITE_JITSI_DOMAIN}`,
      },
      serviceUrl: `wss://me.${import.meta.env.VITE_JITSI_FRONTEND_DOMAIN}/xmpp-websocket?room=${this.#jitsi.post.id}`,
    })

    this.#connection.addEventListener(
      this.#client.events.connection.CONNECTION_ESTABLISHED,
      this.#onConnected
    )
    this.#connection.addEventListener(
      this.#client.events.connection.CONNECTION_FAILED,
      this.#jitsi.onConnectionFailed
    )
    this.#connection.addEventListener(
      this.#client.events.connection.CONNECTION_DISCONNECTED,
      this.#jitsi.onDisconnected
    )

    console.log('[Jitsi] Connecting...')
    this.#connection.connect()
  }

  reset = async () => {
    this.#checkPaymentScheduler.reset()

    if (this.#room) {
      this.#room.removeEventListener(
        this.#client.events.conference.CONFERENCE_JOINED,
        this.#onRoomJoined
      )
      this.#room.removeEventListener(
        this.#client.events.conference.CONFERENCE_ERROR,
        this.#jitsi.onError
      )
      this.#room.removeEventListener(this.#client.events.conference.USER_JOINED, this.#onUserJoined)
      this.#room.removeEventListener(
        this.#client.events.conference.USER_LEFT,
        this.#jitsi.onUserLeft
      )
      this.#room.removeEventListener(
        this.#client.events.conference.DISPLAY_NAME_CHANGED,
        this.#onDisplayNameChanged
      )
      this.#room.removeEventListener(
        this.#client.events.conference.USER_ROLE_CHANGED,
        this.#onUserRoleChanged
      )

      await this.#room.leave()
      await this.#room.dispose()
    }

    if (this.#connection) {
      this.#connection.removeEventListener(
        this.#client.events.connection.CONNECTION_ESTABLISHED,
        this.#onConnected
      )
      this.#connection.removeEventListener(
        this.#client.events.connection.CONNECTION_FAILED,
        this.#jitsi.onConnectionFailed
      )
      this.#connection.removeEventListener(
        this.#client.events.connection.CONNECTION_DISCONNECTED,
        this.#jitsi.onDisconnected
      )

      await this.#connection.disconnect()
    }

    runInAction(() => {
      this.#connection = null
      this.#room = null
      this.id = ''
      this.isModerator = false
    })
  }

  get analytics() {
    return this.#jitsi.rootStore.analyticsStore
  }

  get auth() {
    return this.#jitsi.rootStore.authStore
  }

  @computed
  get isConnected() {
    return (
      Boolean(this.#connection) &&
      Boolean(
        this.#connection.xmpp &&
          this.#connection.xmpp.connectionTimes &&
          this.#connection.xmpp.connectionTimes.connected
      )
    )
  }

  endConference = async () => {
    if (!this.#room || !this.isModerator) return

    try {
      this.#room.getParticipants().forEach(x => {
        try {
          const id = x.getId()
          if (id && id !== this.id) this.#room.kickParticipant(id, 'Conference ended')?.then()
        } catch {
          // noop
        }
      })
    } catch {
      // noop
    }
  }

  syncLocalUserData = () => {
    this.#room.setDisplayName(this.auth.user.nickname)

    if (this.auth.user.hasStore)
      this.#room.setLocalParticipantProperty('avatar-url', this.auth.user.avatar)
  }

  @action
  #onConnected = () => {
    this.#room = this.#connection.initJitsiConference(this.#jitsi.post.id, {
      openBridgeChannel: true,
    })

    this.id = this.#room.myUserId()
    this.isModerator = this.#room.isModerator()

    this.syncLocalUserData()
    if (this.isModerator) this.#room.setSubject(this.#jitsi.post.title)

    this.#jitsi.onConnected()

    this.#room.addEventListener(
      this.#client.events.conference.CONFERENCE_JOINED,
      this.#onRoomJoined
    )
    this.#room.addEventListener(
      this.#client.events.conference.CONFERENCE_ERROR,
      this.#jitsi.onError
    )
    this.#room.addEventListener(this.#client.events.conference.USER_JOINED, this.#onUserJoined)
    this.#room.addEventListener(this.#client.events.conference.USER_LEFT, this.#jitsi.onUserLeft)
    this.#room.addEventListener(
      this.#client.events.conference.DISPLAY_NAME_CHANGED,
      this.#onDisplayNameChanged
    )
    this.#room.addEventListener(
      this.#client.events.conference.USER_ROLE_CHANGED,
      this.#onUserRoleChanged
    )

    console.log('[Jitsi] Joining room...')
    this.#room.join()
  }

  #onRoomJoined = () => {
    if (this.#jitsi.post.isMeeting || this.#jitsi.post.isLivestream)
      this.#room.getParticipants().forEach(x => {
        const id = x.getId()
        if (id !== this.id) this.#jitsi.setUser(id, x.getDisplayName())
      })

    if (
      this.#jitsi.post.isOneTimePayment &&
      !this.#jitsi.post.purchased &&
      !this.#jitsi.post.user.isAuthUser
    )
      this.#checkPaymentScheduler.start(true).then()

    this.#jitsi.onRoomJoined()
  }

  #onUserJoined = (id, participant) => {
    this.#ensurePostOwnerIsModerator()
    this.#jitsi.onUserJoined(id, participant.getDisplayName())
  }

  #onDisplayNameChanged = (id, participant) => {
    this.#jitsi.setUser(id, participant.getDisplayName(), true)
  }

  @action
  #onUserRoleChanged = (id, role) => {
    if (id === this.id) this.isModerator = role === 'moderator'
    this.#ensurePostOwnerIsModerator()
  }

  #ensurePostOwnerIsModerator = () => {
    if (this.isModerator && !this.#jitsi.post.user.isAuthUser)
      this.#room.grantModerator(this.#jitsi.hostUser.jitsiId)
  }

  #checkPayment = async () => {
    console.log('[Jitsi] Checking payment status...')

    const response = await this.#jitsi.api.getPaymentStatus(this.#jitsi.post.id)
    if (!response?.data) return

    if (Number(response.data[0]?.paid ?? 0) !== 1) {
      console.log('[Jitsi] Payment still awaiting confirmation', response.data)

      return
    }

    console.log('[Jitsi] Payment confirmed')
    this.analytics.receivePaymentSuccess()?.then()
    this.#checkPaymentScheduler.stop()
    this.#jitsi.leave()
  }
}
